import { Box, SimpleGrid, Wrap } from "@chakra-ui/react";
import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from "firebase/firestore";
import randomColor from "randomcolor";
import { useEffect, useState } from "react";
import { db } from "../firebase";

const numCols = 16;
const backupColor = "#edf0f5";

export const PixelGrid = () => {
  const { grid, placePixel } = usePixelGrid();

  const [color, setColor] = useState("red");
  useEffect(() => {
    setColor(randomColor());
  }, []);

  return (
    <Wrap width={numCols * 2 * 3 * 4} spacing={0}>
      {[1, 2, 3, 4].map((idx) => (
        <SimpleGrid
          key={idx}
          columns={numCols}
          spacing={0}
          transform={`rotate(${90 * (idx < 3 ? idx : idx === 4 ? 3 : 4)}deg)`}
        >
          {grid.map((row) =>
            row.map((pixel) => (
              <PixelBox
                key={`${pixel.x}+${pixel.y}`}
                pixel={pixel}
                onClick={() =>
                  placePixel({
                    ...pixel,
                    color,
                  })
                }
              />
            ))
          )}
        </SimpleGrid>
      ))}
    </Wrap>
  );
};

const PixelBox = ({
  pixel,
  onClick,
}: {
  pixel: Pixel;
  onClick: () => void;
}) => {
  return (
    <Box
      w={3}
      h={3}
      cursor="pointer"
      bgColor={pixel.color}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    />
  );
};

type Pixel = {
  color: string;
  x: number; // left to right, 0-15
  y: number; // top to bottom, 0-15
};
type PixelPlacement = {
  createdAt: Timestamp;
  pixel: Pixel;
};
const placementsRef = collection(db, "pixel-placements");
export const usePixelGrid = () => {
  // store data, create grid
  const [placements, setPlacements] = useState<PixelPlacement[]>([]);
  const grid = placementsToGrid(placements);
  useEffect(() => {
    const q = query(placementsRef, orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const placements: PixelPlacement[] = [];
      snapshot.forEach((doc) => {
        placements.push(doc.data() as PixelPlacement);
      });
      setPlacements(placements);
    });
    return unsubscribe;
  }, []);

  // update a pixel
  const placePixel = async (pixel: Pixel) => {
    await addDoc(placementsRef, {
      pixel,
      createdAt: Timestamp.now(),
    });
  };

  // reset / delete all placements
  const reset = async () => {
    const snapshot = await getDocs(placementsRef);
    snapshot.forEach(async (doc) => await deleteDoc(doc.ref));
  };

  return { grid, placePixel, reset };
};

const placementsToGrid = (placements: PixelPlacement[]): Pixel[][] => {
  const grid: Pixel[][] = [];
  for (let x = 0; x < numCols; x++) {
    const row: Pixel[] = [];
    for (let y = 0; y < numCols; y++) {
      const placement: PixelPlacement | undefined = placements.find(
        (p) => p.pixel.x === x && p.pixel.y === y
      );
      const color = placement?.pixel.color || backupColor;
      row.push({ x, y, color });
    }
    grid.push(row);
  }
  return grid;
};
